import { useCallback, useEffect, useRef, useState } from 'react';
import { GripVertical, Plus, Trash2, Loader2, ClipboardCopy, RotateCcw, Eye } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { lohnBuchungsbelegApi, fmtMoney } from '../../services/lohnBuchungsbelegApi';
import { bcPasteDifferenceRappen, buildBcRows, buildBcClipboard, copyToClipboard } from '../../services/lohnBcExport';
import { trackFeature } from '../../services/usage';
import type { AccountVatDto, BcExportColumn, BcExportConfig, BcSourceKey, RunDetail } from '../../types/lohnBuchungsbeleg';

interface Props { detail: RunDetail | null; canEdit: boolean; }

/** Human labels for the column-content dropdown. */
const SOURCE_OPTIONS: { key: BcSourceKey; label: string }[] = [
    { key: 'account', label: 'Kontonr.' },
    { key: 'costCenter', label: 'Cost Center Code (9000-Regel)' },
    { key: 'businessUnit', label: 'Business Unit Code (9000-Regel)' },
    { key: 'soll', label: 'Sollbetrag' },
    { key: 'haben', label: 'Habenbetrag' },
    { key: 'betrag', label: 'Betrag (Soll − Haben)' },
    { key: 'betragMw', label: 'Betrag (MW)' },
    { key: 'text', label: 'Text / Beschreibung' },
    { key: 'buchungsdatum', label: 'Buchungsdatum' },
    { key: 'mwstGeschaeft', label: 'MWST-Geschäftsbuchungsgruppe (pro Konto)' },
    { key: 'mwstProdukt', label: 'MWST-Produktbuchungsgruppe (pro Konto)' },
    { key: 'const', label: 'Fester Wert …' },
    { key: 'blank', label: '(leer)' },
];

function newColId(): string {
    return `col-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4)}`;
}

/**
 * BC-Export tab — edits the configurable column definition that shapes the
 * "In Zwischenablage kopieren (BC-Format)" copy, and offers a live preview + copy.
 *
 * BC's "Fibu Erfassungsjournale" grid pastes by COLUMN POSITION, so the order (drag&drop),
 * per-column content (source) and reference labels are fully configurable — the copy stays
 * correct even if the BC layout changes, without a redeploy. The whole config is persisted
 * as one JSON blob per company on every change (optimistic, like the cost-center reorder).
 */
export function BcExportTab({ detail, canEdit }: Props) {
    const { toast } = useToast();
    const [config, setConfig] = useState<BcExportConfig | null>(null);
    const [vatAccounts, setVatAccounts] = useState<AccountVatDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [copyBusy, setCopyBusy] = useState(false);
    const [dragId, setDragId] = useState<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    // Ref mirror so text-input blur handlers persist the latest config without a stale closure.
    const cfgRef = useRef<BcExportConfig | null>(null);
    useEffect(() => { cfgRef.current = config; }, [config]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Template comes along for the per-account MWST groups (preview + copy lookup).
            const [layout, template] = await Promise.all([
                lohnBuchungsbelegApi.getBcExportLayout(),
                lohnBuchungsbelegApi.getTemplate(),
            ]);
            setConfig(layout);
            setVatAccounts(template.vatAccounts ?? []);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'BC-Layout konnte nicht geladen werden', 'error');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { void load(); }, [load]);

    /** Persist a config; on error revert to the server state. */
    const save = useCallback(async (next: BcExportConfig) => {
        setSaving(true);
        try {
            await lohnBuchungsbelegApi.saveBcExportLayout(next);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Speichern fehlgeschlagen', 'error');
            void load();
        } finally {
            setSaving(false);
        }
    }, [toast, load]);

    /** Set state and persist (for discrete actions: reorder, toggle, select, add, delete, options). */
    const applyAndSave = useCallback((next: BcExportConfig) => {
        setConfig(next);
        void save(next);
    }, [save]);

    /** Local-only patch (for text typing); persisted on blur via saveNow(). */
    const patch = useCallback((mut: (c: BcExportConfig) => BcExportConfig) => {
        setConfig(prev => (prev ? mut(prev) : prev));
    }, []);

    const saveNow = useCallback(() => { if (cfgRef.current) void save(cfgRef.current); }, [save]);

    // ---- column mutations ----
    const patchColumn = (id: string, p: Partial<BcExportColumn>) =>
        patch(c => ({ ...c, columns: c.columns.map(col => col.id === id ? { ...col, ...p } : col) }));

    const setColumn = (id: string, p: Partial<BcExportColumn>) => {
        if (!config) return;
        applyAndSave({ ...config, columns: config.columns.map(col => col.id === id ? { ...col, ...p } : col) });
    };

    const addColumn = () => {
        if (!config) return;
        applyAndSave({ ...config, columns: [...config.columns, { id: newColId(), source: 'blank', label: 'Neue Spalte', enabled: true }] });
    };

    const deleteColumn = (id: string) => {
        if (!config) return;
        applyAndSave({ ...config, columns: config.columns.filter(col => col.id !== id) });
    };

    const resetToDefault = async () => {
        if (!confirm('Spaltendefinition auf das Standard-BC-Layout zurücksetzen?')) return;
        try {
            const def = await lohnBuchungsbelegApi.getBcExportLayout({ default: true });
            applyAndSave(def);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Zurücksetzen fehlgeschlagen', 'error');
        }
    };

    const onDrop = (targetId: string) => {
        setDragId(null); setDragOverId(null);
        if (!config || dragId === null || dragId === targetId) return;
        const cols = config.columns;
        const from = cols.findIndex(c => c.id === dragId);
        const to = cols.findIndex(c => c.id === targetId);
        if (from < 0 || to < 0 || from === to) return;
        const next = [...cols];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        applyAndSave({ ...config, columns: next });
    };

    if (loading) {
        return <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
    }
    if (!config) {
        return <div className="rounded border border-dashed border-slate-200 px-3 py-10 text-center text-sm text-slate-400">BC-Layout nicht verfügbar.</div>;
    }

    const enabledCount = config.columns.filter(c => c.enabled).length;
    const preview = detail ? buildBcRows(detail.voucher, config, detail.run, vatAccounts) : null;

    const copy = async () => {
        if (!detail) return;
        setCopyBusy(true);
        try {
            const tsv = buildBcClipboard(detail.voucher, config, detail.run, vatAccounts);
            const rowCount = preview?.rows.length ?? 0;
            const ok = await copyToClipboard(tsv);
            if (ok) trackFeature('lohn-buchungsbeleg.bc-copy-export', { rows: rowCount });
            const diffRappen = bcPasteDifferenceRappen(detail.voucher);
            if (ok && diffRappen !== 0) {
                toast(`Kopiert, aber Σ Soll und Σ Haben weichen nach Rundung um ${fmtMoney(diffRappen / 100, false)} ab — vor dem Buchen in BC prüfen.`, 'error');
            } else {
                toast(ok ? `${rowCount} Buchungszeilen ins BC-Format kopiert` : 'Kopieren fehlgeschlagen', ok ? 'success' : 'error');
            }
        } finally {
            setCopyBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Intro */}
            <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-lg font-semibold text-slate-800">BC-Export — Spaltendefinition</h2>
                    {saving && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> speichern…</span>}
                </div>
                <p className="mt-1 max-w-3xl text-xs text-slate-500">
                    Der Button «In Zwischenablage (BC)» im Tab Buchungsbeleg kopiert die Buchungssätze Tab-getrennt in
                    genau dieser Spaltenreihenfolge — passend zum Einfügen in die Dynamics-BC-Maske «Fibu Erfassungsjournale»
                    (BC fügt spaltenpositions-basiert ein). Reihenfolge per <GripVertical className="inline h-3 w-3" />-Griff
                    ziehen, Spalten umbenennen (nur zur Referenz) und Inhalt zuordnen. Kostenstellen 9100/9200/9300 werden
                    automatisch zu Cost Center 9000 + Business Unit.
                </p>
            </div>

            {/* Column editor */}
            <section>
                <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-700">Spalten ({enabledCount} aktiv / {config.columns.length})</h3>
                    {canEdit && (
                        <div className="flex items-center gap-1.5">
                            <button onClick={addColumn} className="inline-flex items-center gap-1 rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700">
                                <Plus className="h-3.5 w-3.5" /> Spalte
                            </button>
                            <button onClick={resetToDefault} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">
                                <RotateCcw className="h-3.5 w-3.5" /> Standard
                            </button>
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full text-xs">
                        <thead className="bg-slate-100 text-slate-600">
                            <tr>
                                {canEdit && <th className="w-6 px-1 py-1.5" />}
                                <th className="w-10 px-2 py-1.5 text-center">#</th>
                                <th className="w-10 px-2 py-1.5 text-center" title="Aktiv (im Kopiervorgang enthalten)">Aktiv</th>
                                <th className="px-3 py-1.5 text-left">Spaltenname (Referenz)</th>
                                <th className="px-3 py-1.5 text-left">Inhalt</th>
                                <th className="px-3 py-1.5 text-left">Fester Wert</th>
                                {canEdit && <th className="w-10 px-2 py-1.5" />}
                            </tr>
                        </thead>
                        <tbody>
                            {config.columns.map((col, idx) => (
                                <tr
                                    key={col.id}
                                    draggable={canEdit}
                                    onDragStart={() => setDragId(col.id)}
                                    onDragOver={e => { if (canEdit && dragId !== null) { e.preventDefault(); setDragOverId(col.id); } }}
                                    onDrop={e => { e.preventDefault(); onDrop(col.id); }}
                                    onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                                    className={[
                                        'border-t border-slate-100',
                                        dragOverId === col.id && dragId !== col.id ? 'bg-amber-50 ring-1 ring-inset ring-amber-300' : 'hover:bg-slate-50',
                                        dragId === col.id ? 'opacity-40' : '',
                                        col.enabled ? '' : 'text-slate-300',
                                    ].join(' ')}
                                >
                                    {canEdit && (
                                        <td className="px-1 py-1.5 text-center text-slate-300">
                                            <GripVertical className="mx-auto h-3.5 w-3.5 cursor-grab active:cursor-grabbing" />
                                        </td>
                                    )}
                                    <td className="px-2 py-1.5 text-center font-mono text-slate-400">{idx + 1}</td>
                                    <td className="px-2 py-1.5 text-center">
                                        <input
                                            type="checkbox"
                                            checked={col.enabled}
                                            disabled={!canEdit}
                                            onChange={e => setColumn(col.id, { enabled: e.target.checked })}
                                            className="h-3.5 w-3.5 accent-amber-600"
                                        />
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <input
                                            value={col.label}
                                            disabled={!canEdit}
                                            onChange={e => patchColumn(col.id, { label: e.target.value })}
                                            onBlur={saveNow}
                                            className="w-52 rounded border border-slate-200 px-2 py-1 text-xs focus:border-amber-400 focus:outline-none disabled:bg-transparent disabled:border-transparent"
                                        />
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <select
                                            value={col.source}
                                            disabled={!canEdit}
                                            onChange={e => setColumn(col.id, { source: e.target.value as BcSourceKey })}
                                            className="w-56 rounded border border-slate-200 bg-white px-2 py-1 text-xs focus:border-amber-400 focus:outline-none disabled:bg-transparent"
                                        >
                                            {SOURCE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {col.source === 'const' ? (
                                            <input
                                                value={col.value ?? ''}
                                                disabled={!canEdit}
                                                placeholder="z.B. Zahlung"
                                                onChange={e => patchColumn(col.id, { value: e.target.value })}
                                                onBlur={saveNow}
                                                className="w-40 rounded border border-slate-200 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none disabled:bg-transparent disabled:border-transparent"
                                            />
                                        ) : (
                                            <span className="text-slate-300">—</span>
                                        )}
                                    </td>
                                    {canEdit && (
                                        <td className="px-2 py-1.5 text-right">
                                            <button onClick={() => deleteColumn(col.id)} title="Spalte entfernen" className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Options */}
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600">
                    <label className="inline-flex items-center gap-1.5">
                        <input type="checkbox" checked={config.includeHeader} disabled={!canEdit}
                            onChange={e => applyAndSave({ ...config, includeHeader: e.target.checked })}
                            className="h-3.5 w-3.5 accent-amber-600" />
                        Kopfzeile mitkopieren
                        <span className="text-slate-400">(für BC-Paste normalerweise aus)</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                        Zahlenformat
                        <select value={config.numberFormat} disabled={!canEdit}
                            onChange={e => applyAndSave({ ...config, numberFormat: e.target.value as 'swiss' | 'plain' })}
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-xs focus:border-amber-400 focus:outline-none">
                            <option value="swiss">Schweiz (19'507.00)</option>
                            <option value="plain">Ohne Tausender (19507.00)</option>
                        </select>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                        Datumsformat
                        <input value={config.dateFormat} disabled={!canEdit}
                            onChange={e => patch(c => ({ ...c, dateFormat: e.target.value }))} onBlur={saveNow}
                            className="w-28 rounded border border-slate-200 px-2 py-1 font-mono text-xs focus:border-amber-400 focus:outline-none" />
                    </label>
                </div>
            </section>

            {/* Preview + copy */}
            <section>
                <div className="mb-2 flex items-center justify-between">
                    <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Eye className="h-4 w-4 text-slate-400" /> Vorschau</h3>
                    {detail && (
                        <button onClick={copy} disabled={copyBusy} className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50">
                            {copyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ClipboardCopy className="h-3.5 w-3.5" />} In Zwischenablage (BC)
                        </button>
                    )}
                </div>
                {!detail ? (
                    <div className="rounded border border-dashed border-slate-200 px-3 py-8 text-center text-sm text-slate-400">
                        Wähle im Tab Zuordnung einen Lauf, um die Vorschau zu sehen.
                    </div>
                ) : preview && preview.rows.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="min-w-full whitespace-nowrap font-mono text-[11px]">
                            <thead className="bg-slate-100 text-slate-500">
                                <tr>{preview.header.map((h, i) => <th key={i} className="px-2 py-1 text-left font-semibold">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {preview.rows.slice(0, 8).map((r, ri) => (
                                    <tr key={ri} className="border-t border-slate-100">
                                        {r.map((v, ci) => <td key={ci} className="px-2 py-0.5 text-slate-600">{v}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {preview.rows.length > 8 && (
                            <div className="border-t border-slate-100 bg-slate-50 px-2 py-1 text-[11px] text-slate-400">… und {preview.rows.length - 8} weitere Zeilen ({preview.rows.length} total)</div>
                        )}
                    </div>
                ) : (
                    <div className="rounded border border-dashed border-slate-200 px-3 py-8 text-center text-sm text-slate-400">Keine Buchungssätze für diesen Lauf.</div>
                )}
            </section>
        </div>
    );
}
